import { db } from "./firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

export function fmtHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
}


export function getQuestionDurationSeconds(q, fallbackTimer = 0) {
  const raw = q?.TIME ?? q?.time ?? fallbackTimer ?? 0;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

const DISPLAY_LIKE_NO_GATE_TIMER = new Set([
  "DISPLAY",
  "SLIDE",
  "DEFAULT",
  "STARTROUND",
  "ENDROUND",
  "SHOWROUNDS",
  "INTRO",
  "INTRODUCTION",
  "JOIN QUIZ",
  "END QUIZ",
  "BREAK",
  "BREAK TIME",
]);

/** Timed questions wait for hero/TTS; countdown starts when answers gate opens. */
export function shouldDeferTimerUntilAnswersGate(q) {
  if (!getQuestionDurationSeconds(q, 0)) return false;
  const type = String(q?.type ?? q?.CAT ?? "").trim().toUpperCase();
  return !DISPLAY_LIKE_NO_GATE_TIMER.has(type);
}

export function timerActiveOnQuestionAdvance(q, hasTimer) {
  if (!hasTimer) return false;
  return !shouldDeferTimerUntilAnswersGate(q);
}

export async function openAnswersGate(gameCode, current, { timerSeconds = 0 } = {}) {
  try {
    const payload = {
      answersGateQ: current,
      answersGateAt: serverTimestamp(),
    };
    const sec = Math.max(0, Number(timerSeconds) || 0);
    if (sec > 0) {
      payload.timer = sec;
      payload.timerActive = true;
      payload.questionStartTime = Date.now();
      payload.questionStartTimeServer = serverTimestamp();
    }
    await updateDoc(doc(db, "games", gameCode), payload);
  } catch (err) {
    console.error("Failed to open answers gate:", err);
  }
}

export async function speakAndWait(
  line,
  {
    delayMs = 500,
    minHoldMs = 1100,
    maxMs = 15000,
    noDetectGraceMs = 1200,
    approxMinMs = 2000,
    approxPerWordMs = 400,
    postEndHoldMs = 0,
    silenceHoldMs = 300,
    pollStepMs = 120
  } = {}
) {
  return new Promise((resolve) => {
    let done = false;
    const startAtRef = { t: 0 };

    let pollId = 0, approxId = 0, hardCapId = 0, noDetectId = 0, observer = null;

    const cleanup = () => {
      try { if (pollId)     clearInterval(pollId); } catch {}
      try { if (approxId)   clearTimeout(approxId); } catch {}
      try { if (hardCapId)  clearTimeout(hardCapId); } catch {}
      try { if (noDetectId) clearTimeout(noDetectId); } catch {}
      try { if (observer)   observer.disconnect(); } catch {}
      pollId = approxId = hardCapId = noDetectId = 0; observer = null;
    };

    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      window.__mediaBusy = false; // <<< not busy anymore
      const elapsed = performance.now() - (startAtRef.t || performance.now());
      const left = Math.max(0, minHoldMs - elapsed);
      window.setTimeout(resolve, left + Math.max(0, postEndHoldMs));
    };

    const start = () => {
      startAtRef.t = performance.now();
      window.__mediaBusy = true; // <<< mark busy during narration

      // Deduplicate the same line in ~2s (StrictMode-safe)
      try {
        const now = performance.now();
        const last = window.__lastSpoken || { text: "", t: 0 };
        if (String(last.text) === String(line) && (now - last.t) < 2000) {
          window.__mediaBusy = false;
          return finish();
        }
        window.__lastSpoken = { text: String(line), t: now };
      } catch {}

      try { if (typeof window.aiStop === "function") window.aiStop(); } catch {}

      try {
        if (typeof window.aiSpeak === "function") {
          try { window.aiSpeak(line, { onend: finish }); }
          catch { window.aiSpeak(line); }
        }
      } catch {}

      let sawSpeaking = false;
      let silenceMs = 0;
      const SILENCE_HOLD = Math.max(0, silenceHoldMs);
      const STEP = Math.max(60, pollStepMs);

      const markSpeaking = () => { sawSpeaking = true; silenceMs = 0; };

      const isAnyAudioPlaying = () => {
        try {
          const audios = Array.from(document.querySelectorAll("audio"));
          return audios.some(a => {
            const src = (a.currentSrc || a.src || "").toLowerCase();
            const isBed = a.loop || a.getAttribute("data-bed") === "1" || src.includes("/sfx/396595_valo_0-120.mp3");
            if (isBed) return false;
            return !a.paused && !a.ended && a.currentTime > 0;
          });
        } catch { return false; }
      };

      const hookAudio = (el) => {
        if (!el || el.__ttsHooked) return;
        el.__ttsHooked = true;
        try {
          el.addEventListener("playing", markSpeaking, { passive: true });
          el.addEventListener("timeupdate", () => {
            if (!el.paused && !el.ended) markSpeaking();
          }, { passive: true });
          el.addEventListener("ended", finish, { once: true, passive: true });
        } catch {}
      };

      try { Array.from(document.querySelectorAll("audio")).forEach(hookAudio); } catch {}
      try {
        observer = new MutationObserver((muts) => {
          for (const m of muts) {
            m.addedNodes && m.addedNodes.forEach((n) => {
              if (n && n.nodeType === 1) {
                if (n.tagName === "AUDIO") hookAudio(n);
                n.querySelectorAll && n.querySelectorAll("audio").forEach(hookAudio);
              }
            });
          }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
      } catch {}

      pollId = window.setInterval(() => {
        let speaking = false;
        try {
          if (window.speechSynthesis) {
            speaking = window.speechSynthesis.speaking || window.speechSynthesis.pending;
          }
        } catch {}
        if (!speaking) speaking = isAnyAudioPlaying();

        if (speaking) {
          sawSpeaking = true;
          silenceMs = 0;
        } else if (sawSpeaking) {
          silenceMs += STEP;
          if (silenceMs >= SILENCE_HOLD) finish();
        }
      }, STEP);

      noDetectId = window.setTimeout(() => {
        if (!sawSpeaking) {
          const words = Math.max(1, String(line).trim().split(/\s+/).length);
          const estimate = Math.min(maxMs, Math.max(approxMinMs, words * approxPerWordMs));
          approxId = window.setTimeout(finish, estimate);
        }
      }, Math.max(300, noDetectGraceMs));

      hardCapId = window.setTimeout(finish, maxMs + 500);
    };

    window.setTimeout(start, Math.max(0, delayMs));
  });
}

export function isMultiChoiceType(t) {
  const T = String(t || "").toUpperCase();
  return (
    T === "MULTICHOICE" ||
    T === "MULTIPLECHOICE" ||
    T === "MULTI" ||
    T === "MCQ" ||
    T === "MULTICHOICE_SINGLE" ||
    T === "MULTIPLE_CHOICE"
  );
}

export function withTTSSuppressed(cb) {
  const origAiSpeak = window.aiSpeak;
  const origWebSpeechSpeak =
    (window.speechSynthesis && window.speechSynthesis.speak) || null;

  try {
    // Hard-mute both paths during the callback
    window.aiSpeak = () => {};
    if (window.speechSynthesis && typeof window.speechSynthesis.speak === "function") {
      window.speechSynthesis.speak = () => {};
    }
    cb && cb();
  } finally {
    // Restore
    window.aiSpeak = origAiSpeak;
    if (origWebSpeechSpeak && window.speechSynthesis) {
      window.speechSynthesis.speak = origWebSpeechSpeak;
    }
  }
}

export function seededShuffle(arr, seed) {
  let a = arr.slice();
  let m = a.length, t, i;
  let s = seed;
  while (m) {
    i = Math.floor(Math.abs(Math.sin(s++)) * m--);
    t = a[m];
    a[m] = a[i];
    a[i] = t;
  }
  return a;
}

export function normalizeType(q) {
  const raw = String(q?.type || q?.CAT || "").trim().toUpperCase();

  const displayAliases = new Set([
    "DISPLAY", "SLIDE", "DEFAULT", "STARTROUND", "ENDROUND", "SHOWROUNDS",
    "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME"
  ]);

  if (displayAliases.has(raw)) return "DISPLAY";

  const known = [
    "MULTICHOICE", "TRUE/FALSE", "TRUEFALSE", "MULTIANSWER",
    "NEARESTANSWER", "TYPE", "BREADCRUMBS", "BETANSWER", "WHEEL", "PICTURE",
    "AUDIO", "VIDEO", "PUZZLE", "VANISHINGCHOICE", "OUTOFORDER" 
  ];
  if (known.includes(raw)) return raw.replace("TRUEFALSE", "TRUE/FALSE");

  const answers = Array.isArray(q?.answers)
    ? q.answers
    : [q?.A1, q?.A2, q?.A3, q?.A4, q?.A5, q?.A6].filter(v => v !== undefined);
  const noAnswers = answers.every(a => String(a ?? "").trim() === "");
  const t = parseInt(q?.time ?? q?.TIME ?? 0, 10) || 0;
  if (noAnswers && t === 0) return "DISPLAY";

  return raw || "DISPLAY";
}

export function getClampedAnswersOrder(q) {
  const t = normalizeType(q);
  const order = Array.isArray(q?.answersOrder) ? q.answersOrder.slice() : [];
  const answers = Array.isArray(q?.answers) ? q.answers : [];

  if (t === "TRUE/FALSE" || t === "TRUEFALSE") {
    const allowed = [0, 1].filter(i => answers[i] != null && String(answers[i]).trim() !== "");
    const filtered = order.filter(i => allowed.includes(i));
    return (filtered.length ? filtered : allowed).slice(0, 2);
  }

  return order;
}

export function getTypeTheme(q) {
  switch (normalizeType(q)) {
    case "MULTICHOICE": // green
      return { bgFrom:"rgba(20,120,60,.62)", bgTo:"rgba(12,80,40,.58)", border:"rgba(72,221,124,.6)", pillFrom:"#1b5e32", pillTo:"#114822", dotFrom:"#c8ffd9", dotTo:"#49e58a" };
       case "MULTIANSWER": // ORANGE
      return { bgFrom: "rgba(230,126,34,0.62)", bgTo:   "rgba(210,110,24,0.58)", border: "rgba(243,156,18,0.60)", pillFrom: "#ab5e12", pillTo: "#8b4a0e", dotFrom: "#fff0d6", dotTo: "#ffd08a" };
        case "BETANSWER": // amber
      return { bgFrom:"rgba(160,110,0,.62)", bgTo:"rgba(110,70,0,.58)", border:"rgba(255,208,92,.6)", pillFrom:"#6c4a0a", pillTo:"#513805", dotFrom:"#ffe9b0", dotTo:"#ffc64d" };
    case "TYPE": // purple
      return { bgFrom:"rgba(90,35,140,.60)", bgTo:"rgba(60,22,95,.56)", border:"rgba(202,170,255,.6)", pillFrom:"#4f267e", pillTo:"#3b1a61", dotFrom:"#e8d7ff", dotTo:"#c09bff" };
    case "BREADCRUMBS": // brown/tan
      return { bgFrom:"rgba(141,110,99,.62)", bgTo:"rgba(109,76,65,.58)", border:"rgba(161,136,127,.6)", pillFrom:"#5d4037", pillTo:"#4e342e", dotFrom:"#d7ccc8", dotTo:"#a1887f" };
    case "NEARESTANSWER": // deep purple
      return { bgFrom:"rgba(110,35,170,.64)", bgTo:"rgba(72,18,120,.58)", border:"rgba(210,170,255,.68)", pillFrom:"#5b2396", pillTo:"#3e1670", dotFrom:"#f0e3ff", dotTo:"#bf98ff" };
    case "WHEEL": // indigo
      return { bgFrom:"rgba(45,56,150,.60)", bgTo:"rgba(28,34,100,.56)", border:"rgba(162,172,255,.6)", pillFrom:"#303c92", pillTo:"#242d72", dotFrom:"#dadaff", dotTo:"#a8afff" };
    case "TRUE/FALSE": // LEMON
      return { bgFrom: "rgba(244,208,63,0.62)", bgTo: "rgba(223,186,45,0.58)", border: "rgba(247,220,111,0.60)", pillFrom: "#6a5605", pillTo: "#524203", dotFrom: "#fff5b0", dotTo: "#ffe566" };
    case "OUTOFORDER": // apricot
    return { bgFrom: "rgba(255, 200, 162, 0.62)", bgTo: "rgba(255, 170, 120, 0.58)", border: "rgba(255, 218, 185, 0.60)", pillFrom: "#e6a97f", pillTo: "#d68f63", dotFrom: "#fff0e6", dotTo: "#ffd1b5" };
      default: // fallback navy
      return { bgFrom:"rgba(16,22,36,.58)", bgTo:"rgba(10,14,24,.54)", border:"rgba(128,168,255,.5)", pillFrom:"#2a3f6a", pillTo:"#1b2b4d", dotFrom:"#d4e3ff", dotTo:"#7aa2ff" };
  }
}

export function buildBackgroundCSS(url, isDisplay) {
  if (!url) return undefined;
  return isDisplay
    ? `url(${url}) center center / cover no-repeat`
    : `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${url}) center center / cover no-repeat`;
}

export function cleanQuestionText(question) {
  if (!question) return "";
  return question
    .replace(/^\{B\}\s*/i, "")
    .replace(/^\{Q(?:,TXT|,NUM)?\}\s*/i, "")
    .replace(/^\{TXT\}\s*/i, "")
    .replace(/^\{NUM\}\s*/i, "")
    .replace(/^\{E\}\s*/i, "")
    .replace(/\{LB\}/gi, "\n")
    .trim();
}

function getMultiAnswerSuffix(q) {
  if ((q.type || "").toUpperCase() !== "MULTIANSWER") return "";
  const count = q.answers.filter(a => /^\s*\[.*?\]\s*$/.test(a)).length;
  if (count > 1) {
    return ` (SELECT ${count})`;
  }
  return "";
}

export function buildQuestionDisplayText(q) {
  return cleanQuestionText(q.question) + getMultiAnswerSuffix(q);
}

export function getTypeDisplayName(q) {
  const type = String(q?.type || q?.CAT || "").toUpperCase();
  switch (type) {
    case "MULTICHOICE":     return "Multiple Choice";
    case "MULTIANSWER":     return "Multi Answer";
    case "BETANSWER":       return "Bet Your Points";
    case "TYPE":            return "Type Your Answer";
    case "BREADCRUMBS":     return "Breadcrumbs";
    case "NEARESTANSWER":   return "Nearest Answer";
    case "WHEEL":           return "Wheel of Fortune";
    case "TRUE/FALSE":      return "TRUE or FALSE";
    case "VANISHINGCHOICE": return "Vanishing Choice";
     case "OUTOFORDER":      return "Out of Order";
    case "BREAK TIME":      return "Break Time";
    case "INTRODUCTION":    return "Introduction";
    case "JOIN QUIZ":       return "Join Quiz";
    case "END QUIZ":        return "End of Quiz";
    default:                return "Question";
  }
}

export function getRulesForQuestion(q) {
  const rawCustom = q?.RULES_TEXT || q?.RULES || q?.rules_text || q?.rules || "";
  if (rawCustom && String(rawCustom).trim()) {
    return String(rawCustom)
      .replace(/\{LB\}/gi, "\n")
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  const t = normalizeType(q);
  const ans = Array.isArray(q?.answers) ? q.answers : [];
  const multiPickCount = ans.filter(a => /^\s*\[.*?\]\s*$/.test(String(a ?? ""))).length;

  switch (t) {
    case "MULTICHOICE": return ["Select one answer", "No penalty points or bonus points"];
    case "VANISHINGCHOICE": return ["Wrong answers vanish over time", "DO NOT GUESS! Penalty points apply"];
    case "OUTOFORDER": return ["Drag the items into the correct sequence", "Points are awarded for each item in the correct position"];
    case "MULTIANSWER": return [multiPickCount > 1 ? `Select ${multiPickCount} answers` : "Select multiple answers", "Select multiple answers for each question", "Each correct answer is rewarded"];
    case "BETANSWER": return ["Place a bet before answering", "Correct answer wins your bet, wrong loses it", "Bet smart for maximum return"];
    case "TYPE": return ["You're allowed the odd typo!", "More points for faster answers"];
    case "BREADCRUMBS": return ["Clues reveal evenly over time", "Guess the answer; earlier clue = more points", "Unlimited guesses until correct or time runs out"];
    case "NEARESTANSWER": return ["Guess a number closest to the answer", "Nearest 5 teams score points", "Fastest answers break ties"];
    case "WHEEL": return ["Letters reveal over time", "Solve the phrase at any moment", "Earlier solves earn more points"];
    case "TRUE/FALSE": return ["Choose True or False"];
    default: return ["Answer as fast as you can", "Highest points early, then tapering"];
  }
}

export function countCorrectOptions(q) {
  const ans = Array.isArray(q?.answers) ? q.answers : [];
  if (ans.length && typeof ans[0] === "object" && ans[0] !== null) {
    return ans.filter(a => {
      const flag = a && (a.isCorrect === true || a.correct === true || a.is_right === true);
      if (flag) return true;
      const s = String(a.text ?? a.label ?? a.value ?? "").trim();
      return (/^\[.*?\]$/.test(s) || /[✓]\s*$/.test(s) || /\(correct\)\s*$/i.test(s));
    }).length;
  }
  let count = 0;
  for (const a of ans) {
    const s = String(a ?? "").trim();
    if (!s) continue;
    if (/^\[.*?\]$/.test(s)) { count++; continue; }
    if (/[✓]\s*$/.test(s)) { count++; continue; }
    if (/\(correct\)\s*$/i.test(s)) { count++; continue; }
  }
  return count;
}

export function getTimerPhase(q, timer) {
  const total = parseInt(q.time ?? q.TIME ?? 0, 10) || 0;
  let phaseTotal = total;
  let phaseRemaining = Math.max(0, timer);
  let phaseLabel = null;
  if (String(q.type || "").toUpperCase() === "BETANSWER") {
    if (timer > 30) {
      phaseTotal = Math.max(1, total - 30);
      phaseRemaining = Math.max(0, timer - 30);
      phaseLabel = "Bet";
    } else {
      phaseTotal = 30;
      phaseRemaining = Math.max(0, timer);
      phaseLabel = "Answer";
    }
  }
  const progress = Math.max(0, Math.min(1, phaseRemaining / (phaseTotal || 1)));
  return { progress, phaseLabel, phaseTotal, phaseRemaining };
}

/** JIGSAW column: cover the picture with tiles that clear as the clock runs. Off unless set to Yes/On/true/1. */
export function isJigsawOn(q) {
  const raw = q?.JIGSAW ?? q?.jigsaw ?? "";
  return ["yes", "on", "true", "1"].includes(String(raw).trim().toLowerCase());
}

export function hashSeed(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return hash >>> 0;
}

function mulberry32(a) {
  return function() {
    var t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

export function seededShuffleJigsaw(arr, seed = 1) {
  let a = arr.slice();
  let random = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function buildWheelBoard(answer, rows = 3, cols = 12) {
  const cleaned = answer
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return Array.from({ length: rows }, () => Array(cols).fill(""));

  const words = cleaned.split(" ");
  const board = Array.from({ length: rows }, () => Array(cols).fill(""));
  let row = 0;
  let col = 0;

  for (let w = 0; w < words.length; w++) {
    let word = words[w];
    while (word.length > cols) {
      if (col !== 0) { row++; col = 0; }
      for (let i = 0; i < cols && word.length; i++) {
        board[row][col++] = word[0];
        word = word.slice(1);
      }
      row++; col = 0;
      if (row >= rows) return board;
    }
    if ((col === 0 && word.length <= cols) || (col > 0 && (col + 1 + word.length) <= cols)) {
      if (col > 0) board[row][col++] = " ";
      for (let i = 0; i < word.length; i++) {
        board[row][col++] = word[i];
      }
    } else {
      row++;
      col = 0;
      if (row >= rows) return board;
      for (let i = 0; i < word.length && col < cols; i++) {
        board[row][col++] = word[i];
      }
    }
  }
  return board;
}

export function computeAnswerFontSize(text) {
  const len = String(text || "").trim().length;
  if (len <= 18) return "clamp(28px, 4.2vw, 56px)";
  if (len <= 28) return "clamp(24px, 3.6vw, 48px)";
  if (len <= 40) return "clamp(22px, 3.2vw, 42px)";
  if (len <= 56) return "clamp(20px, 2.6vw, 36px)";
  if (len <= 80) return "clamp(18px, 2.3vw, 32px)";
  return "clamp(16px, 2.0vw, 28px)";
}


// === Background preload cache with budgets and concurrency ===
const BG_MAX_DECODED_BYTES = 120 * 1024 * 1024; // ~120 MiB total decoded budget
const BG_MAX_ITEMS = 12;                         // hard cap on number of cached backgrounds
const BG_MAX_CONCURRENCY = 3;                    // how many to fetch/decode at once

// LRU map: url -> { promise, width, height, bytes }
const bgEntries = (typeof window !== "undefined"
  ? (window.__bgEntries ||= new Map())
  : new Map());

function lruTouch(url) {
  const v = bgEntries.get(url);
  if (!v) return;
  bgEntries.delete(url);
  bgEntries.set(url, v); // move to end (most recent)
}

function lruEvictIfNeeded() {
  let total = 0;
  for (const [, v] of bgEntries) total += v.bytes || 0;

  while ((bgEntries.size > BG_MAX_ITEMS || total > BG_MAX_DECODED_BYTES) && bgEntries.size) {
    const [oldUrl, oldVal] = bgEntries.entries().next().value;
    total -= oldVal.bytes || 0;
    bgEntries.delete(oldUrl);
  }
}

function ensurePreloadLink(url) {
  if (!url) return;
  const key = `preload-${url}`;
  if (document.querySelector(`link[data-preload="${key}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("data-preload", key);
  document.head.appendChild(link);
}

// Decode-aware preloader that respects LRU budgets
function preloadBackground(url) {
  if (!url) return Promise.resolve();
  const existing = bgEntries.get(url);
  if (existing?.promise) { lruTouch(url); return existing.promise; }

  // Create once, store immediately to dedupe
  const img = new Image();
  img.src = url;

  const p = (img.decode ? img.decode() : new Promise(res => {
    img.onload = res; img.onerror = res; // fail-open to avoid blocking
  })).then(() => {
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    const bytes = w * h * 4; // RGBA in memory
    bgEntries.set(url, { promise: p, width: w, height: h, bytes });
    lruTouch(url);
    lruEvictIfNeeded();
    return url;
  }).catch(() => url); // non-fatal

  bgEntries.set(url, { promise: p, width: 0, height: 0, bytes: 0 });
  return p;
}

// Preload a list with small parallelism
export function preloadBackgroundsLimited(urls) {
  return new Promise(resolve => {
    const queue = urls.slice();
    let active = 0;
    let done = 0;

    const next = () => {
      if (done === urls.length) return resolve();
      while (active < BG_MAX_CONCURRENCY && queue.length) {
        const u = queue.shift();
        active++;
        ensurePreloadLink(u);
        preloadBackground(u).finally(() => {
          active--; done++; next();
        });
      }
    };
    next();
  });
}

/** Vanishing choice: wrong answers vanish evenly over full question time until minChoices remain. */

export function finalizeVanishingIndexes(visibleIndexes, minChoices = 2) {
  const visible = Array.isArray(visibleIndexes) ? [...visibleIndexes] : [];
  if (visible.length <= minChoices) return visible;
  const correctIdx = 0;
  const kept = [];
  if (visible.includes(correctIdx)) kept.push(correctIdx);
  for (const idx of visible) {
    if (kept.length >= minChoices) break;
    if (!kept.includes(idx)) kept.push(idx);
  }
  return kept.slice(0, minChoices);
}

/**
 * Build a timer-synced vanishing plan from full question duration (starts at answers gate).
 * vanishAtRemaining[i]: countdown (seconds left) when vanish i triggers.
 * All vanishes complete before the final segment so exactly minChoices stay visible
 * for the last (totalSec / vanishCount) seconds of the question.
 */
export function buildVanishingChoicePlan(
  totalQuestionSec,
  baseOrder,
  vanishOrder,
  minChoices = 2
) {
  const order = Array.isArray(baseOrder) ? [...baseOrder] : [];
  const vanishCount = Math.max(0, order.length - minChoices);
  const totalSec = Math.max(1, Number(totalQuestionSec) || 0);

  const vanishAtRemaining = [];
  if (vanishCount > 0) {
    const finalSegment = totalSec / vanishCount;
    const activePeriod = Math.max(0, totalSec - finalSegment);
    for (let i = 0; i < vanishCount; i++) {
      const elapsedAtVanish = ((i + 1) * activePeriod) / vanishCount;
      vanishAtRemaining.push(Math.max(0, totalSec - elapsedAtVanish));
    }
  }

  return {
    totalSec,
    vanishAtRemaining,
    vanishOrder: (Array.isArray(vanishOrder) ? vanishOrder : []).slice(0, vanishCount),
    baseOrder: order,
    minChoices,
  };
}

/** Derive visible answer indexes from synced countdown timer + plan (host and player). */
export function computeVanishingIndexesFromPlan(plan, timerRemainingSec) {
  if (!plan || !Array.isArray(plan.baseOrder)) return [];
  const timer = Math.max(0, Number(timerRemainingSec) || 0);
  const minChoices = plan.minChoices ?? 2;
  let visible = [...plan.baseOrder];

  const vanishOrder = plan.vanishOrder || [];
  const thresholds = plan.vanishAtRemaining || [];
  for (let i = 0; i < vanishOrder.length; i++) {
    const threshold = thresholds[i];
    if (typeof threshold !== "number") continue;
    if (timer <= threshold) {
      const idx = vanishOrder[i];
      visible = visible.filter((x) => x !== idx);
    }
  }

  if (timer <= 0 && visible.length > minChoices) {
    return finalizeVanishingIndexes(visible, minChoices);
  }
  return visible;
}

